import { FC, useEffect } from "react";
import Drawer from "@mui/material/Drawer";
import { CompanyPage } from "./CompanyPage";
import {
  InstallFilters,
  IntentFilters,
  useCompanyPageStore,
} from "../../store/companyPageStore";
import { CompanyTab } from "./types";
import type { SpogCompany } from "@/legacy/types/SpogCompany";

export type CompanyScoresMap = Record<string, SpogCompany["scores"]>;

interface CompanyDrawerProps {
  open: boolean;
  onClose: () => void;
  companyId: string | null;
  companyIds: string[];
  installFilters: InstallFilters;
  intentFilters?: IntentFilters;
  scoresMap?: CompanyScoresMap;
}

export const CompanyDrawer: FC<CompanyDrawerProps> = ({
  open,
  onClose,
  companyId,
  companyIds,
  installFilters,
  intentFilters,
  scoresMap,
}) => {
  const setDrawerMode = useCompanyPageStore((state) => state.setDrawerMode);
  const setNavigationContext = useCompanyPageStore(
    (state) => state.setNavigationContext
  );
  const resetCompanyData = useCompanyPageStore(
    (state) => state.resetCompanyData
  );
  const setActiveTab = useCompanyPageStore((state) => state.setActiveTab);
  const currentCompanyId = useCompanyPageStore(
    (state) => state.navigation.currentCompanyId
  );

  useEffect(() => {
    if (open && companyId && companyIds.length > 0) {
      setNavigationContext(companyIds, companyId);
      setDrawerMode(true);
    }
    return () => {
      if (open) {
        setDrawerMode(false);
      }
    };
  }, [open, companyId, companyIds, setDrawerMode, setNavigationContext]);

  const handleClose = () => {
    onClose();
    resetCompanyData();
    setActiveTab(CompanyTab.SUMMARY);
    setDrawerMode(false);
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={handleClose}
      slotProps={{
        paper: {
          sx: {
            width: {
              md: "90vw", // 80% on medium screens
              lg: "min(90vw, 1400px)", // Max 1400px on large screens
              xl: "min(80vw, 1600px)", // Max 1600px on extra large
            },
            maxWidth: "1600px", // Absolute maximum
            minWidth: "320px",
          },
        },
      }}
    >
      {currentCompanyId && (
        <CompanyPage
          companyId={currentCompanyId}
          onClose={handleClose}
          installFilters={installFilters}
          intentFilters={intentFilters}
          companyScores={scoresMap?.[currentCompanyId]}
        />
      )}
    </Drawer>
  );
};
